import React, { useState, useEffect, useRef, memo } from "react";
import {
  FiChevronDown,
  FiGlobe,
  FiMoon,
  FiSettings,
  FiX,
  FiFolder,
} from "react-icons/fi";
import { HiMenu } from "react-icons/hi";
import { FaEthereum } from "react-icons/fa";
import {
  MdOutlineCandlestickChart,
  MdCropFree,
  MdOutlineCameraAlt,
} from "react-icons/md";

import logo from "../Assets/logo-31.png";
import Card from "../Assets/card.svg";

// Embedded TradingView Chart Widget Component
const EmbeddedChart = memo(({ symbol = "BINANCE:ETHUSDT" }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = "";

    const script = document.createElement("script");
    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.type = "text/javascript";
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: symbol,
      interval: "5",
      timezone: "Etc/UTC",
      theme: "dark",
      style: "1",
      locale: "en",
      backgroundColor: "#0d0e12",
      gridColor: "rgba(255, 255, 255, 0.03)",
      hide_side_toolbar: false,
      allow_symbol_change: true,
      save_image: false,
      calendar: false,
      hide_volume: false,
      support_host: "https://www.tradingview.com",
    });

    containerRef.current.appendChild(script);
  }, [symbol]);

  return (
    <div
      className="absolute inset-0 w-full h-full tradingview-widget-container"
      ref={containerRef}
    >
      <div className="w-full h-full tradingview-widget-container__widget"></div>
    </div>
  );
});

export default function Home({ setShowModal }) {
  const [showBanner, setShowBanner] = useState(true);
  const [orderType, setOrderType] = useState("Buy");

  return (
    <div className="min-h-screen bg-[#0d0e12] text-[#d1d4dc] flex flex-col font-sans select-none overflow-x-hidden">
      {/* 1. Desktop Navigation Header */}
      <nav className="h-12 px-3 bg-[#0d0e12] border-b border-[#1c1e24] flex items-center justify-between text-xs font-medium">
        {/* Left Brand & Nav */}
        <div className="flex items-center gap-6">
          <div className="flex items-center cursor-pointer">
            <div className="flex items-center justify-center w-8 h-8 text-xs font-bold text-black rounded-sm">
              <img
                src={logo}
                alt="Lighter Logo"
                className="object-contain w-full h-full"
              />
            </div>
            <span className="text-sm tracking-wide text-white">Lighter</span>
          </div>

          <div className="items-center hidden gap-5 text-gray-400 lg:flex">
            <button className="text-white transition hover:text-white">
              Trade
            </button>
            <button className="transition hover:text-white">Portfolio</button>
            <button className="transition hover:text-white">
              Public Pools
            </button>
            <button className="transition hover:text-white">Staking</button>
            <button className="transition hover:text-white">Referrals</button>
            <button className="transition hover:text-white">Leaderboard</button>
            <button className="transition hover:text-white">Tools</button>
            <button className="flex items-center gap-1 transition hover:text-white">
              More <FiChevronDown size={12} />
            </button>
          </div>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 bg-[#16181e] border border-[#232730] px-2.5 py-1 rounded-md text-[11px] text-gray-400">
            <span>BLOCK</span>
            <span className="font-mono text-white">348,584,947</span>
          </div>

          <div className="items-center hidden gap-1 text-gray-400 sm:flex">
            <button className="p-1.5 hover:text-white hover:bg-[#16181e] rounded-md transition">
              <FiGlobe size={15} />
            </button>
            <button className="p-1.5 hover:text-white hover:bg-[#16181e] rounded-md transition">
              <FiMoon size={15} />
            </button>
            <button className="p-1.5 hover:text-white hover:bg-[#16181e] rounded-md transition">
              <FiSettings size={15} />
            </button>
            <button className="p-1.5 hover:text-white hover:bg-[#16181e] rounded-md transition">
              <FiFolder size={15} />
            </button>
          </div>

          <button
            onClick={() => setShowModal?.(true)}
            className="bg-white hover:bg-gray-200 text-black font-semibold text-xs px-3 py-1.5 rounded transition active:scale-95"
          >
            Connect Wallet
          </button>

          <button className="p-1.5 text-gray-400 hover:text-white lg:hidden">
            <HiMenu size={18} />
          </button>
        </div>
      </nav>

      {/* 2. Top Deposit Announcement Banner */}
      {showBanner && (
        <div className="bg-[#12141a] border-b border-[#1c1e24] px-4 py-2 flex items-center justify-between text-xs relative">
          <div className="flex items-center justify-center gap-2.5 text-center sm:text-left select-none">
            <div className="flex items-center justify-center shrink-0">
              <img
                src={Card}
                alt="Payment methods"
                className="object-contain h-6 sm:h-7 w-auto max-w-[90px]"
              />
            </div>
            <p className="text-xs font-medium leading-normal text-gray-300">
              Deposits from Card, Bitcoin, Coinbase, Tron, Solana, USDT and
              more, are now available.
            </p>
          </div>

          <button
            onClick={() => setShowBanner(false)}
            className="p-1 text-gray-500 transition rounded hover:text-white"
          >
            <FiX size={14} />
          </button>
        </div>
      )}

      {/* 3. Market Header & Metrics Bar */}
      <div className="bg-[#0d0e12] border-b border-[#1c1e24] px-4 py-2 flex items-center justify-between text-xs overflow-x-auto scrollbar-none min-w-max">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 bg-[#16181e] px-2.5 py-1 rounded-md border border-[#232730] cursor-pointer hover:bg-[#1f222b]">
            <FaEthereum className="text-[#627eea] text-base" />
            <span className="text-sm font-bold text-white">ETH</span>
            <span className="text-xs text-gray-400">50x</span>
            <FiChevronDown className="text-xs text-gray-400" />
          </div>

          <div className="flex items-baseline gap-2">
            <span className="text-base font-extrabold text-white">
              2,723.93
            </span>
            <span className="text-[#00c087] font-semibold text-xs flex items-center gap-0.5">
              +1.22% <FiChevronDown className="rotate-180" size={10} />
            </span>
          </div>

          <div className="hidden md:flex items-center gap-6 text-[11px]">
            <div>
              <div className="text-gray-500">MARK PRICE</div>
              <div className="text-gray-200 font-mono mt-0.5">2,723.99</div>
            </div>
            <div>
              <div className="text-gray-500">INDEX PRICE</div>
              <div className="text-gray-200 font-mono mt-0.5">2,725.20</div>
            </div>
            <div>
              <div className="text-gray-500">24H CHANGE</div>
              <div className="text-[#00c087] font-semibold mt-0.5">+1.23%</div>
            </div>
            <div>
              <div className="text-gray-500">24H VOLUME</div>
              <div className="text-gray-200 font-mono mt-0.5">$184.65M</div>
            </div>
            <div>
              <div className="text-gray-500">OPEN INTEREST</div>
              <div className="text-gray-200 font-mono mt-0.5">$250.01M</div>
            </div>
            <div>
              <div className="text-gray-500">1HR FUNDING</div>
              <div className="text-[#00c087] font-mono mt-0.5">+0.0012%</div>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Sub-Navigation Tabs */}
      <div className="bg-[#0d0e12] border-b border-[#1c1e24] px-4 py-1.5 flex items-center gap-4 text-xs font-medium text-gray-400">
        <button className="text-white bg-[#191b22] px-3 py-1 rounded-md border border-[#262a36]">
          Price
        </button>
        <button className="transition hover:text-white">Order Book</button>
        <button className="transition hover:text-white">Trades</button>
        <button className="transition hover:text-white">Depth</button>
        <button className="transition hover:text-white">Funding</button>
        <button className="transition hover:text-white">Details</button>
      </div>

      {/* 5. Main Workspace Grid */}
      <div className="flex flex-col flex-1 overflow-hidden lg:flex-row">
        {/* Left Column: Chart & Positions Panel */}
        <div className="flex-1 flex flex-col border-r border-[#1c1e24] min-h-[480px]">
          <div className="h-8 px-3 bg-[#0d0e12] border-b border-[#1c1e24] flex items-center justify-between text-[11px] text-gray-400">
            <div className="flex items-center gap-3">
              <span>5m</span>
              <span className="cursor-pointer hover:text-white">15m</span>
              <span className="cursor-pointer hover:text-white">1h</span>
              <span className="cursor-pointer hover:text-white">4h</span>
              <span className="hover:text-white cursor-pointer flex items-center gap-0.5">
                More <FiChevronDown size={10} />
              </span>
              <div className="h-3 w-[1px] bg-[#232730]" />
              <MdOutlineCandlestickChart className="text-sm cursor-pointer hover:text-white" />
              <span className="cursor-pointer hover:text-white">
                Chart Elements
              </span>
            </div>

            <div className="flex items-center gap-2">
              <MdCropFree className="cursor-pointer hover:text-white" />
              <MdOutlineCameraAlt className="cursor-pointer hover:text-white" />
            </div>
          </div>

          <div className="flex-1 relative w-full bg-[#0d0e12]">
            <EmbeddedChart />
          </div>

          <div className="bg-[#0d0e12] border-t border-[#1c1e24]">
            <div className="flex items-center gap-5 px-4 py-2 text-xs border-b border-[#1c1e24] text-gray-400">
              <button className="text-white font-semibold bg-[#191b22] px-2.5 py-1 rounded border border-[#262a36]">
                Positions
              </button>
              <button className="transition hover:text-white">Assets</button>
              <button className="transition hover:text-white">
                Open Orders
              </button>
              <button className="transition hover:text-white">TWAP</button>
              <button className="transition hover:text-white">
                Order History
              </button>
              <button className="transition hover:text-white">
                Trade History
              </button>
            </div>
            <div className="p-6 text-xs text-center text-gray-500">
              No open positions
            </div>
          </div>
        </div>

        {/* Center Column: Order Book (Desktop Only) */}
        <div className="hidden xl:flex w-64 bg-[#0d0e12] border-r border-[#1c1e24] flex-col text-[11px]">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#1c1e24] font-semibold text-gray-400">
            <span>Order Book</span>
            <span>Trades</span>
          </div>

          <div className="grid grid-cols-2 px-3 py-1 text-gray-500 text-[10px]">
            <span>PRICE</span>
            <span className="text-right">SIZE ETH</span>
          </div>

          <div className="flex-1 overflow-y-auto font-mono text-[11px] space-y-0.5 px-3 py-1">
            {[
              { price: "2,725.1", size: "45.1984" },
              { price: "2,725.0", size: "33.0338" },
              { price: "2,724.9", size: "155.7616" },
              { price: "2,724.8", size: "22.4848" },
              { price: "2,724.7", size: "117.5362" },
            ].map((row, i) => (
              <div key={i} className="flex justify-between">
                <span className="text-[#ff4d4d]">{row.price}</span>
                <span className="text-gray-300">{row.size}</span>
              </div>
            ))}
          </div>

          <div className="px-3 py-1 bg-[#12141a] border-y border-[#1c1e24] flex justify-between items-center text-[10px]">
            <span className="font-mono font-bold text-white">2,723.93</span>
            <span className="text-gray-500">SPREAD 0.10 (0.004%)</span>
          </div>

          <div className="flex-1 overflow-y-auto font-mono text-[11px] space-y-0.5 px-3 py-1">
            {[
              { price: "2,723.6", size: "1.0327" },
              { price: "2,723.5", size: "2.8034" },
              { price: "2,723.4", size: "18.6372" },
              { price: "2,723.3", size: "29.8885" },
              { price: "2,723.2", size: "164.8535" },
            ].map((row, i) => (
              <div key={i} className="flex justify-between">
                <span className="text-[#00c087]">{row.price}</span>
                <span className="text-gray-300">{row.size}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Execution Form (Desktop Only) */}
        <div className="hidden lg:flex w-72 bg-[#0d0e12] flex-col p-3 text-xs space-y-3">
          <div className="grid grid-cols-2 bg-[#16181e] p-1 rounded text-xs font-semibold">
            <button
              onClick={() => setOrderType("Buy")}
              className={`py-2 rounded-md transition ${
                orderType === "Buy"
                  ? "bg-[#00c087] text-black shadow"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              Buy / Long
            </button>
            <button
              onClick={() => setOrderType("Sell")}
              className={`py-2 rounded-md transition ${
                orderType === "Sell"
                  ? "bg-[#ff4d4d] text-white shadow"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              Sell / Short
            </button>
          </div>

          <div className="flex items-center justify-between px-1 text-xs text-gray-400">
            <div className="flex gap-3">
              <span className="font-semibold text-white cursor-pointer">
                Market
              </span>
              <span className="cursor-pointer hover:text-white">Limit</span>
            </div>
            <span className="text-gray-500 flex items-center gap-0.5 cursor-pointer hover:text-gray-300">
              Advanced <FiChevronDown size={10} />
            </span>
          </div>

          <div className="space-y-1">
            <div className="flex justify-between text-[11px] text-gray-400">
              <span>Amount</span>
              <span>0.0000 ETH</span>
            </div>
            <div className="flex items-center bg-[#16181e] border border-[#232730] rounded-lg px-3 py-2">
              <input
                type="text"
                placeholder="0"
                className="w-full font-mono text-sm text-white bg-transparent outline-none"
              />
              <span className="ml-2 text-xs font-semibold text-gray-400">
                ETH
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 py-1">
            <div className="flex-1 h-1.5 bg-[#1e222b] rounded-full relative">
              <div className="absolute left-0 top-0 bottom-0 w-0 bg-[#00c087] rounded-full" />
            </div>
            <span className="text-[10px] text-gray-400 font-mono">0%</span>
          </div>

          <button
            onClick={() => setShowModal?.(true)}
            className="w-full bg-white hover:bg-gray-200 text-black font-semibold py-3 rounded text-xs transition active:scale-[0.99] shadow-sm mt-2"
          >
            Connect Wallet to Trade
          </button>

          <div className="space-y-1.5 pt-3 border-t border-[#1c1e24] text-[11px] text-gray-400">
            <div className="flex justify-between">
              <span>Position Margin</span>
              <span className="font-mono text-white">$0.00</span>
            </div>
            <div className="flex justify-between">
              <span>Slippage</span>
              <span className="font-mono text-white">Est: 0.00% | Max: 1%</span>
            </div>
            <div className="flex justify-between">
              <span>Fees</span>
              <span className="font-mono text-white">
                Taker: 0% | Maker: 0%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 6. Floating Action Bottom Buttons (Mobile View) */}
      <div className="lg:hidden fixed bottom-3 left-1/2 -translate-x-1/2 z-40 w-[94%] max-w-md">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowModal?.(true)}
            className="flex-1 bg-[#3DFFB2] hover:bg-[#0fec94] active:scale-[0.99] transition-all text-black font-bold py-3 rounded text-base shadow-lg shadow-emerald-500/10 text-center"
          >
            Buy / Long
          </button>

          <button
            onClick={() => setShowModal?.(true)}
            className="flex-1 bg-[#FF003F] hover:bg-[#ca0133] active:scale-[0.99] transition-all text-white font-bold py-3 rounded text-base shadow-lg shadow-red-500/10 text-center"
          >
            Sell / Short
          </button>
        </div>
      </div>
    </div>
  );
}
